import { Terminology } from "./Terminology";
import { HexColor } from "./UI";

export const WorkingDays = {
  Sunday: "Sunday",
  Monday: "Monday",
  Tuesday: "Tuesday",
  Wednesday: "Wednesday",
  Thursday: "Thursday",
  Friday: "Friday",
  Saturday: "Saturday",
} as const;

export type WorkingDays = (typeof WorkingDays)[keyof typeof WorkingDays];

export type WorkingDay = {
  startTime: string;
  endTime: string;
  isClosed: boolean;
};

export type WorkingHours = { [key in WorkingDays]: WorkingDay };

export type PromiseTimeConfig = {
  pastDueColor?: HexColor;
};

export type ShopConfig = {
  // eslint-disable-next-line no-unused-vars
  workingHours?: WorkingHours;
  dashboardScaling?: "scaleToFit" | "noScaling";
  /**
   * Shows the linked-label and linked-technician icons on board container
   * headers. Off by default — the linkage is configured (and still shown) in
   * Layout Builder, so on the board the icons were only eating header width.
   */
  showContainerLinkIcons?: boolean;
  /**
   * Shows the card's DMS-synced label (Card.label) as an extra tag-styled
   * pill on the board card, sorted first among the card's real tags. Off by
   * default. A per-card override (Card.labelAsTagEnabled) takes precedence
   * over this shop default when set.
   */
  showLabelsAsTags?: boolean;
  /**
   * Shows the technician initials badge (and the service advisor popover it
   * carries) on the board card. On by default; turning it off frees the card
   * header, where the badges can cover the key tag number.
   */
  showCardTechnician?: boolean;
  /**
   * Shows every technician on the card rather than only one. On by default.
   * Off means a single badge: the RO's technician if assigned, otherwise the
   * first job technician. Moot while `showCardTechnician` is off.
   */
  showAdditionalCardTechnicians?: boolean;
  /**
   * Shop-admin opt-in for syncing a card's assigned technician back to
   * Tekmetric on move. Independent of, and layered on top of, the internal
   * `FEATURE_FLAGS.isTechnicianWritebackEnabled` engineering flag — both
   * must be true for a write to fire (see `isTechnicianWritebackEnabledForShop`
   * in src/lib/technicianWriteback.ts). Off by default.
   */
  technicianWritebackEnabled?: boolean;
  terminology?: Terminology;
  useShopHeroEmployeeJobTitle?: boolean;
  customerUpdates?: CustomerUpdatesConfig;
  inboxPanel?: InboxPanelConfig;
  promiseTime?: PromiseTimeConfig;
  /** IANA time zone, e.g. "America/Chicago". Interprets scheduled automations. */
  timeZone?: string;
};

export type TimeOption = {
  label: string;
  value: number; // minutes
};

export type CustomerUpdatesConfig = {
  enabled: boolean;
  defaultCompletedOnTimeBufferMin: number; // Default buffer in minutes for customer updates to be considered completed on time
  defaultSnoozeBufferMin: number;
  requireComments?: boolean;
  autoCreateCustomerUpdates?: boolean; // Whether to automatically create customer updates when a card is created
  quickTimeOptions?: TimeOption[]; // Configurable quick time options for customer updates
};

export type InboxPanelConfig = {
  showActivityFeed: boolean;
  showCustomerUpdates: boolean;
  showGoList: boolean;
  showMessages: boolean;
  showDispatchView?: boolean;
};
